import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import CommercialLayout from "../../components/layouts/CommercialLayout";
import api from "../../services/api";
import imageCompression from "browser-image-compression";

const couleurScore = (score) => {
  if (score >= 80) return { bg: "#f0fdf4", text: "#16a34a", label: "Très fiable" };
  if (score >= 60) return { bg: "#fefce8", text: "#ca8a04", label: "Fiable" };
  if (score >= 40) return { bg: "#fff7ed", text: "#ea580c", label: "À risque" };
  return { bg: "#fef2f2", text: "#dc2626", label: "Bloqué" };
};

const statutBadge = (statut) => {
  const map = {
    "Fiable":      { bg: "#fefce8", text: "#ca8a04" },
    "Tres fiable": { bg: "#f0fdf4", text: "#16a34a" },
    "A risque":    { bg: "#fff7ed", text: "#ea580c" },
    "Bloque":      { bg: "#fef2f2", text: "#dc2626" },
    "Archive":     { bg: "#f3f4f6", text: "#6b7280" },
  };
  return map[statut] || { bg: "#f3f4f6", text: "#6b7280" };
};

// ── Compression image ──
const compresserImage = async (file) => {
  const options = {
    maxSizeMB: 0.9,          // max 900 KB
    maxWidthOrHeight: 1280,  // max 1280px
    useWebWorker: true,
    fileType: "image/jpeg",  // convertir en JPEG
  };
  try {
    const compressed = await imageCompression(file, options);
    return compressed;
  } catch {
    return file; // si compression échoue, retourner l'original
  }
};

// ── Modal Créer Client ──
const ModalCreerClient = ({ onClose, onSuccess }) => {
  const [form, setForm] = useState({ nom: "", prenom: "", telephone: "", date_naissance: "", sexe: "", activite: "", cni: "", localisation: "", photo_cni_recto: null, photo_cni_verso: null });
  const [activiteAutre, setActiviteAutre] = useState("");
  const [loading, setLoading] = useState(false);
  const [compressing, setCompressing] = useState({ recto: false, verso: false });
  const [error, setError] = useState("");

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handlePhoto = async (e, champ) => {
    const file = e.target.files[0];
    if (!file) return;
    setCompressing(prev => ({ ...prev, [champ === "photo_cni_recto" ? "recto" : "verso"]: true }));
    const compressed = await compresserImage(file);
    setForm(prev => ({ ...prev, [champ]: compressed }));
    setCompressing(prev => ({ ...prev, [champ === "photo_cni_recto" ? "recto" : "verso"]: false }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const formData = new FormData();
      const formFinal = { ...form, activite: form.activite === "Autre" ? activiteAutre : form.activite };
      Object.entries(formFinal).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          formData.append(key, value);
        }
      });
      await api.post("/clients", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || "Erreur lors de la création");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: "100%", padding: "0.65rem 0.9rem",
    border: "1px solid #e5e7eb", borderRadius: "8px",
    fontSize: "13px", color: "#111827", outline: "none",
    boxSizing: "border-box", background: "white"
  };

  const renderPhotoField = (champ, label) => {
    const key = champ === "photo_cni_recto" ? "recto" : "verso";
    const isCompressing = compressing[key];
    const photo = form[champ];

    return (
      <div style={{ marginBottom: "0.75rem" }}>
        <label style={{ fontSize: "12px", color: "#6b7280", display: "block", marginBottom: "6px" }}>{label}</label>
        {isCompressing ? (
          <div style={{ padding: "0.75rem", backgroundColor: "#f9fafb", borderRadius: "8px", fontSize: "12px", color: "#6b7280", textAlign: "center" }}>
            Compression en cours...
          </div>
        ) : (
          <div style={{ display: "flex", gap: "8px" }}>
            <label style={{ flex: 1, padding: "0.6rem", border: "1px solid #e5e7eb", borderRadius: "8px", fontSize: "12px", color: "#185FA5", cursor: "pointer", textAlign: "center", backgroundColor: "#E6F1FB", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
              <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
              Choisir fichier
              <input type="file" accept="image/*" onChange={(e) => handlePhoto(e, champ)} style={{ display: "none" }} />
            </label>
            <label style={{ flex: 1, padding: "0.6rem", border: "1px solid #e5e7eb", borderRadius: "8px", fontSize: "12px", color: "#7c3aed", cursor: "pointer", textAlign: "center", backgroundColor: "#f5f3ff", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
              <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
              Prendre photo
              <input type="file" accept="image/*" capture="environment" onChange={(e) => handlePhoto(e, champ)} style={{ display: "none" }} />
            </label>
          </div>
        )}
        {photo && !isCompressing && (
          <div style={{ marginTop: "6px", display: "flex", alignItems: "center", gap: "6px" }}>
            <img src={URL.createObjectURL(photo)} alt={key} style={{ width: "60px", height: "40px", objectFit: "cover", borderRadius: "6px", border: "1px solid #e5e7eb" }} />
            <span style={{ fontSize: "11px", color: "#16a34a" }}>✓ {(photo.size / 1024).toFixed(0)} KB</span>
            <button type="button" onClick={() => setForm(prev => ({ ...prev, [champ]: null }))} style={{ background: "none", border: "none", cursor: "pointer", color: "#dc2626", fontSize: "12px" }}>×</button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.45)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: "16px", padding: "1.5rem", width: "100%", maxWidth: "480px", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
          <h3 style={{ fontSize: "16px", fontWeight: "500", margin: 0, color: "#111827" }}>Nouveau client</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "20px", color: "#6b7280", lineHeight: 1 }}>×</button>
        </div>

        {error && (
          <div style={{ marginBottom: "1rem", padding: "0.75rem", backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px", color: "#dc2626", fontSize: "13px" }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "0.75rem" }}>
            <div>
              <label style={{ fontSize: "12px", color: "#6b7280", display: "block", marginBottom: "4px" }}>Nom *</label>
              <input name="nom" value={form.nom} onChange={handleChange} placeholder="Diallo" required style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: "12px", color: "#6b7280", display: "block", marginBottom: "4px" }}>Prénom *</label>
              <input name="prenom" value={form.prenom} onChange={handleChange} placeholder="Fatou" required style={inputStyle} />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "0.75rem" }}>
            <div>
              <label style={{ fontSize: "12px", color: "#6b7280", display: "block", marginBottom: "4px" }}>Téléphone</label>
              <input name="telephone" value={form.telephone} onChange={handleChange} placeholder="77 123 45 67" style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: "12px", color: "#6b7280", display: "block", marginBottom: "4px" }}>Date de naissance</label>
              <input name="date_naissance" type="date" value={form.date_naissance} onChange={handleChange} style={inputStyle} />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "0.75rem" }}>
            <div>
              <label style={{ fontSize: "12px", color: "#6b7280", display: "block", marginBottom: "4px" }}>Sexe *</label>
              <select name="sexe" value={form.sexe} onChange={handleChange} required style={inputStyle}>
                <option value="">-- Choisir --</option>
                <option value="M">Masculin</option>
                <option value="F">Féminin</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: "12px", color: "#6b7280", display: "block", marginBottom: "4px" }}>CNI *</label>
              <input name="cni" value={form.cni} onChange={handleChange} placeholder="1234567890123" required style={inputStyle} />
            </div>
          </div>

          <div style={{ marginBottom: "0.75rem" }}>
            <label style={{ fontSize: "12px", color: "#6b7280", display: "block", marginBottom: "4px" }}>Activité</label>
            <select name="activite" value={form.activite} onChange={handleChange} style={inputStyle}>
              <option value="">-- Choisir --</option>
              <option value="Vente">Vente</option>
              <option value="Peche">Pêche</option>
              <option value="Transformation">Transformation</option>
              <option value="Autre">Autre</option>
            </select>
            {form.activite === "Autre" && (
              <input
                type="text"
                value={activiteAutre}
                onChange={e => setActiviteAutre(e.target.value)}
                placeholder="Précisez l'activité..."
                style={{ ...inputStyle, marginTop: "8px" }}
              />
            )}
          </div>

          <div style={{ marginBottom: "0.75rem" }}>
            <label style={{ fontSize: "12px", color: "#6b7280", display: "block", marginBottom: "4px" }}>Localisation</label>
            <input name="localisation" value={form.localisation} onChange={handleChange} placeholder="Dakar, Thiès..." style={inputStyle} />
          </div>

          {renderPhotoField("photo_cni_recto", "Photo CNI Recto *")}
          {renderPhotoField("photo_cni_verso", "Photo CNI Verso *")}

          <button type="submit" disabled={loading || compressing.recto || compressing.verso} style={{
            width: "100%", padding: "0.75rem",
            background: "linear-gradient(135deg, #1d4ed8, #185FA5)",
            color: "white", fontWeight: "500", fontSize: "14px",
            borderRadius: "9999px", border: "none",
            cursor: (loading || compressing.recto || compressing.verso) ? "not-allowed" : "pointer",
            opacity: (loading || compressing.recto || compressing.verso) ? 0.7 : 1
          }}>
            {loading ? "Création en cours..." : "Créer le client"}
          </button>
        </form>
      </div>
    </div>
  );
};

// ── Badge état crédit ──
const BadgeCredit = ({ client }) => {
  if (client.credit_non_rembourse) return <span style={{ fontSize: "11px", backgroundColor: "#fef2f2", color: "#dc2626", padding: "2px 8px", borderRadius: "6px", flexShrink: 0, fontWeight: "500" }}>Non remboursé</span>;
  if (client.credit_en_cours) {
    const enRetard = new Date(client.credit_en_cours.echeance) < new Date();
    return <span style={{ fontSize: "11px", backgroundColor: enRetard ? "#fff7ed" : "#E6F1FB", color: enRetard ? "#ea580c" : "#185FA5", padding: "2px 8px", borderRadius: "6px", flexShrink: 0, fontWeight: "500" }}>{enRetard ? "En retard" : "En cours"}</span>;
  }
  if (client.statut === "Bloque") return <span style={{ fontSize: "11px", backgroundColor: "#fef2f2", color: "#dc2626", padding: "2px 8px", borderRadius: "6px", flexShrink: 0 }}>Bloqué</span>;
  return <span style={{ fontSize: "11px", backgroundColor: "#f0fdf4", color: "#16a34a", padding: "2px 8px", borderRadius: "6px", flexShrink: 0 }}>Disponible</span>;
};

// ── Page principale ──
const Clients = () => {
  const { utilisateur } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filtreStatut, setFiltreStatut] = useState("tous");
  const [filtreCredit, setFiltreCredit] = useState("tous");
  const [showModal, setShowModal] = useState(false);

  const fetchClients = async () => {
    try {
      const res = await api.get("/clients");
      setClients(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchClients(); }, [utilisateur]);

  const clientsFiltres = clients.filter((c) => {
    const matchSearch = `${c.nom} ${c.prenom}`.toLowerCase().includes(search.toLowerCase());
    const matchStatut = filtreStatut === "tous" || c.statut === filtreStatut;
    const matchCredit = filtreCredit === "tous"
      || (filtreCredit === "en_cours" && c.credit_en_cours)
      || (filtreCredit === "non_rembourse" && c.credit_non_rembourse)
      || (filtreCredit === "disponible" && !c.credit_en_cours && !c.credit_non_rembourse);
    return matchSearch && matchStatut && matchCredit;
  });

  const nbEnCours = clients.filter(c => c.credit_en_cours).length;
  const nbNonRembourse = clients.filter(c => c.credit_non_rembourse).length;
  const nbDisponible = clients.filter(c => !c.credit_en_cours && !c.credit_non_rembourse && c.statut !== "Archive").length;

  return (
    <CommercialLayout>
      {showModal && (
        <ModalCreerClient onClose={() => setShowModal(false)} onSuccess={() => { setShowModal(false); fetchClients(); }} />
      )}

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.25rem", flexWrap: "wrap", gap: "0.75rem" }}>
        <div>
          <h1 style={{ fontSize: "18px", fontWeight: "500", color: "#111827", margin: 0 }}>Mes clients</h1>
          <p style={{ fontSize: "13px", color: "#6b7280", margin: "4px 0 0" }}>{clients.filter(c => c.statut !== "Archive").length} client(s) actifs</p>
        </div>
        <button onClick={() => setShowModal(true)} style={{ padding: "0.6rem 1.25rem", background: "linear-gradient(135deg, #1d4ed8, #185FA5)", color: "white", fontWeight: "500", fontSize: "13px", borderRadius: "9999px", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px" }}>
          <svg width="14" height="14" fill="none" stroke="white" strokeWidth="2.5" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Nouveau client
        </button>
      </div>

      {/* Stats rapides */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: "10px", marginBottom: "1.25rem" }}>
        {[
          { label: "Crédits en cours", value: nbEnCours, color: "#185FA5", bg: "#E6F1FB" },
          { label: "Non remboursés", value: nbNonRembourse, color: "#dc2626", bg: "#fef2f2" },
          { label: "Disponibles", value: nbDisponible, color: "#0f6e56", bg: "#E1F5EE" },
        ].map((s, i) => (
          <div key={i} style={{ backgroundColor: "white", border: "0.5px solid #e5e7eb", borderRadius: "12px", padding: "0.75rem 1rem" }}>
            <div style={{ fontSize: "11px", color: "#6b7280", marginBottom: "4px" }}>{s.label}</div>
            <div style={{ fontSize: "20px", fontWeight: "500", color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Recherche */}
      <div style={{ position: "relative", marginBottom: "0.75rem" }}>
        <svg width="14" height="14" fill="none" stroke="#9ca3af" strokeWidth="2" viewBox="0 0 24 24" style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)" }}>
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input type="text" placeholder="Rechercher un client..." value={search} onChange={(e) => setSearch(e.target.value)}
          style={{ width: "100%", padding: "0.65rem 0.9rem 0.65rem 2.25rem", border: "1px solid #e5e7eb", borderRadius: "9999px", fontSize: "13px", outline: "none", boxSizing: "border-box", background: "white" }} />
      </div>

      {/* Filtre statut */}
      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "0.75rem" }}>
        {[{ value: "tous", label: "Tous" }, { value: "Fiable", label: "Fiables" }, { value: "Tres fiable", label: "Très fiables" }, { value: "A risque", label: "À risque" }, { value: "Bloque", label: "Bloqués" }].map((f) => (
          <button key={f.value} onClick={() => setFiltreStatut(f.value)} style={{ padding: "0.4rem 0.9rem", borderRadius: "9999px", fontSize: "12px", border: filtreStatut === f.value ? "none" : "1px solid #e5e7eb", backgroundColor: filtreStatut === f.value ? "#185FA5" : "white", color: filtreStatut === f.value ? "white" : "#6b7280", cursor: "pointer" }}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Filtre crédit */}
      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "1.25rem" }}>
        {[{ value: "tous", label: "Tous crédits" }, { value: "en_cours", label: "En cours" }, { value: "non_rembourse", label: "Non remboursé" }, { value: "disponible", label: "Disponible" }].map((f) => (
          <button key={f.value} onClick={() => setFiltreCredit(f.value)} style={{ padding: "0.4rem 0.9rem", borderRadius: "9999px", fontSize: "12px", border: filtreCredit === f.value ? "none" : "1px solid #e5e7eb", backgroundColor: filtreCredit === f.value ? "#1d4ed8" : "white", color: filtreCredit === f.value ? "white" : "#6b7280", cursor: "pointer" }}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Liste */}
      <div style={{ backgroundColor: "white", border: "0.5px solid #e5e7eb", borderRadius: "16px", overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: "3rem", textAlign: "center", color: "#6b7280", fontSize: "14px" }}>Chargement...</div>
        ) : clientsFiltres.length === 0 ? (
          <div style={{ padding: "3rem", textAlign: "center" }}>
            <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>👤</div>
            <p style={{ color: "#6b7280", fontSize: "14px" }}>Aucun client trouvé</p>
          </div>
        ) : (
          clientsFiltres.map((client, i) => {
            const sc = couleurScore(client.score || 60);
            const sb = statutBadge(client.statut);
            return (
              <div key={client.id} onClick={() => navigate(`/commercial/clients/${client.id}`)}
                style={{ display: "flex", alignItems: "center", gap: "12px", padding: "0.9rem 1.25rem", cursor: "pointer", borderBottom: i < clientsFiltres.length - 1 ? "0.5px solid #f3f4f6" : "none" }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f9fafb"}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}>
                <div style={{ width: "40px", height: "40px", borderRadius: "50%", backgroundColor: sc.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px", fontWeight: "500", color: sc.text, flexShrink: 0 }}>
                  {client.nom?.charAt(0)}{client.prenom?.charAt(0)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: "14px", fontWeight: "500", color: "#111827" }}>{client.prenom} {client.nom}</div>
                  <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "2px" }}>{client.telephone || "—"} · {client.activite || "—"}</div>
                </div>
                <div style={{ textAlign: "center", flexShrink: 0 }}>
                  <div style={{ fontSize: "16px", fontWeight: "500", color: sc.text }}>{client.score || 60}</div>
                  <div style={{ fontSize: "10px", color: "#9ca3af" }}>score</div>
                </div>
                <span style={{ fontSize: "11px", padding: "3px 10px", borderRadius: "6px", backgroundColor: sb.bg, color: sb.text, flexShrink: 0 }}>{client.statut}</span>
                <BadgeCredit client={client} />
                <svg width="14" height="14" fill="none" stroke="#9ca3af" strokeWidth="2" viewBox="0 0 24 24" style={{ flexShrink: 0 }}><polyline points="9 18 15 12 9 6"/></svg>
              </div>
            );
          })
        )}
      </div>
    </CommercialLayout>
  );
};

export default Clients;